import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpward, ArrowDownward, Delete, Add } from '@mui/icons-material';
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';

const PORT_POSITIONS = ['top', 'bottom', 'left', 'right'];

const PortDestinations = ({ cell, graph, paper, updatePorts }) => {
  const [ports, setPorts] = useState(cell.getPorts() || []);

  const handlePortChange = (portId, fieldName, newValue) => {
    const updatedPorts = ports.map((port) => {
      if (port.id === portId) {
        return {
          ...port,
          [fieldName]: newValue,
        };
      }
      return port;
    });
    setPorts(updatedPorts);
    updatePorts(updatedPorts, cell.id);

    if (fieldName === 'destination') {
      updatePortLinks(portId);
    }
  };

  const updatePortLinks = (portId) => {
    const existingLinks = graph.getConnectedLinks(cell, {
      outbound: true,
      port: portId,
    });
    existingLinks.forEach((link) => link.remove());
  };

  const handleAddPort = () => {
    const newPort = {
      id: `port-${Date.now()}`,
      group: 'top',
      data: { destination: null, time: 0 },
    };
    const newPorts = [...ports, newPort];
    setPorts(newPorts);
    updatePorts(newPorts, cell.id);
  };

  const handleRemovePort = (portId) => {
    const newPorts = ports.filter((p) => p.id !== portId);
    setPorts([...newPorts]);
    updatePorts([...newPorts], cell.id);
    updatePortLinks(portId);
  };

  const handleReorder = (portId, direction) => {
    const index = ports.findIndex((p) => p.id === portId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= ports.length) return;
    const newPorts = [...ports];
    [newPorts[index], newPorts[newIndex]] = [newPorts[newIndex], newPorts[index]];
    setPorts(newPorts);
    updatePorts(newPorts, cell.id);
  };

  const getLinkOptions = () => {
    const elements = graph.getElements();
    return elements
      .filter((el) => el.id !== cell.id)
      .map((el) => ({
        value: el.id,
        content: el.attr('label/text') || el.id,
      }));
  };

  return (
    <TableContainer component={Paper}>
      <Table
        size="small"
        sx={{
          '& .MuiTableCell-root': {
            px: 1, // 8px padding horizontal
            py: '6px', // Garder le padding vertical par défaut
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Port</TableCell>
            <TableCell>Position</TableCell>
            <TableCell>Destination</TableCell>
            <TableCell>Temps</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ports.map((port, index) => (
            <TableRow key={port.id}>
              <TableCell>Port {index + 1}</TableCell>
              <TableCell>
                <Select
                  value={port.group || 'top'}
                  onChange={(e) => handlePortChange(port.id, 'group', e.target.value)}
                  MenuProps={{ container: () => document.getElementById('map-and-panels-container') }}
                >
                  {PORT_POSITIONS.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position.charAt(0).toUpperCase() + position.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <Select
                  value={port.data?.destination || ''}
                  onChange={(e) => handlePortChange(port.id, 'destination', e.target.value)}
                  MenuProps={{ container: () => document.getElementById('map-and-panels-container') }}
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {getLinkOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.content}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <TextField
                  type="number"
                  value={port.data?.time || 0}
                  onChange={(e) => handlePortChange(port.id, 'time', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  InputProps={{
                    inputProps: { min: 0 },
                    sx: {
                      // eslint-disable-next-line max-len
                      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
                        {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    },
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => handleReorder(port.id, 'up')} disabled={index === 0}>
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleReorder(port.id, 'down')} disabled={index === ports.length - 1}>
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleRemovePort(port.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button variant="outlined" fullWidth onClick={handleAddPort} startIcon={<Add />}>
        Ajouter un port
      </Button>
    </TableContainer>
  );
};

PortDestinations.propTypes = {
  cell: PropTypes.object.isRequired,
  graph: PropTypes.object.isRequired,
  paper: PropTypes.object.isRequired,
  updatePorts: PropTypes.func.isRequired,
};

export default PortDestinations;
